// Dominoes (double-six) — pure rules engine + computer players.
//
// No I/O (it imports only the shared types), so the same file runs in the
// `games` edge function (Deno), in the browser (legal-play hints) and under
// node tests. The GameDef that plugs it into the table framework is at the
// bottom.
//
// Rules (US / Caribbean social "partner dominoes", house defaults):
//  - Double-six set: 28 tiles, 7 each.
//  - 4 seated → partners: North & South vs East & West, all 28 tiles dealt,
//    no boneyard. Can't play → pass ("knock").
//  - 2 seated → head-to-head (3 seated → everyone for themselves): 7 each, the
//    rest is the boneyard. Can't play → draw until you can (or it's empty),
//    then pass.
//  - First hand: whoever holds the double-six sets it (2–3 players: if it's in
//    the boneyard, the highest double held; no double → the heaviest tile).
//    Later hands: the winner of the last hand leads any tile.
//  - Play matches an open end of the line (two ends; doubles are just tiles).
//  - Domino: a player empties their hand → their side wins the hand.
//    Block: nobody can play and the boneyard is empty → the player with the
//    lowest pip count wins for their side (a tie between sides scores nothing).
//  - The winning side scores the pips left in the opponents' hands, rounded to
//    the nearest 5 (12 → 10, 13 → 15).
//  - First side to the target (100 by default; lobby option 100/150/200) wins.
//  - Turns go clockwise (N → E → S → W) through the seated players.

import { RuleError, type GameDef, type SeatResult } from './types.ts';
export { RuleError };

/** A tile is "high-low", e.g. '6-4', '3-3'. */
export type Tile = string;
export type End = 'left' | 'right';

export const SEAT_NAMES = ['North', 'East', 'South', 'West'];
export const TARGETS = [100, 150, 200] as const;
export const DEFAULT_TARGET = 100;
export const HAND_SIZE = 7;
/** Private row index for the boneyard (nobody sits there, so nobody can read it). */
export const BONEYARD_SEAT = 99;

export const tileOf = (a: number, b: number): Tile => (a >= b ? `${a}-${b}` : `${b}-${a}`);
export const pipsOf = (t: Tile): [number, number] => [Number(t[0]), Number(t[2])];
export const isDouble = (t: Tile) => t[0] === t[2];
export const valueOf = (t: Tile) => Number(t[0]) + Number(t[2]);
export const handPips = (h: Tile[]) => h.reduce((s, t) => s + valueOf(t), 0);
export const roundTo5 = (n: number) => Math.round(n / 5) * 5;

/** Normalise untrusted input to a tile string, or null. */
export function parseTile(x: unknown): Tile | null {
  const m = /^([0-6])-([0-6])$/.exec(String(x ?? ''));
  return m ? tileOf(Number(m[1]), Number(m[2])) : null;
}

export function fullSet(): Tile[] {
  const s: Tile[] = [];
  for (let a = 6; a >= 0; a--) for (let b = a; b >= 0; b--) s.push(tileOf(a, b));
  return s;
}

export function shuffle<T>(arr: T[], rng: () => number): T[] {
  const a = arr.slice();
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(rng() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

/** Display order: heaviest first, doubles before the same total. */
export function sortHand(h: Tile[]): Tile[] {
  return h.slice().sort((x, y) => valueOf(y) - valueOf(x) || Number(y[0]) - Number(x[0]));
}

/** A tile on the table. `l`/`r` = the pips facing the left / right of the line. */
export interface Placed { tile: Tile; l: number; r: number; seat: number }

export type LastAction =
  | { seat: number; kind: 'play'; tile: Tile; end: End }
  | { seat: number; kind: 'pass' }
  | { seat: number; kind: 'draw'; drew: number; passed: boolean };

export interface HandResult {
  how: 'domino' | 'block';
  /** Seat that dominoed / had the lowest count (null = tied block). */
  seat: number | null;
  /** Winning side index (null = tied block, nobody scores). */
  side: number | null;
  /** Pips left in each seat's hand (index = seat; inactive seats 0). */
  pips: number[];
  /** Opponents' pips before rounding, and the points scored. */
  raw: number;
  points: number;
  /** The tiles everyone was left holding (public once the hand is over). */
  reveal: Tile[][];
}

export interface DominoesState {
  phase: 'playing' | 'hand_over' | 'game_over';
  mode: 'partners' | 'solo';
  /** Seated seats, in turn order. */
  players: number[];
  /** Sides: partners [[0,2],[1,3]]; solo one seat each. scores[i] belongs to sides[i]. */
  sides: number[][];
  target: number;
  handNo: number;
  turn: number;
  /** The line of play, leftmost first. */
  line: Placed[];
  /** Index in `line` of the first tile set this hand (the layout grows out from it). */
  first: number;
  /** First hand: the tile the opener must set (public — it's announced). */
  mustLead: Tile | null;
  /** Tiles held per seat (public). */
  counts: number[];
  boneyard: number;
  /** Pip values each seat has shown it can't match (passed / drew on). Public knowledge; bots use it. */
  knocks: number[][];
  last: LastAction | null;
  scores: number[];
  lastHand: HandResult | null;
  /** Leads the next hand. */
  nextLeader: number | null;
  winner: number | null;
}

export const sideOf = (g: Pick<DominoesState, 'sides'>, seat: number) => g.sides.findIndex((s) => s.includes(seat));
export const partnerOf = (g: Pick<DominoesState, 'mode'>, seat: number) => (g.mode === 'partners' ? (seat + 2) % 4 : -1);
export function nextPlayer(g: Pick<DominoesState, 'players'>, seat: number): number {
  const i = g.players.indexOf(seat);
  return g.players[(i + 1) % g.players.length];
}
export function ends(g: Pick<DominoesState, 'line'>): [number, number] | null {
  return g.line.length ? [g.line[0].l, g.line[g.line.length - 1].r] : null;
}

/** Where `tile` can go: 'any' on an empty line, else the matching ends. */
export function fitsOn(g: Pick<DominoesState, 'line'>, tile: Tile): End[] {
  const e = ends(g);
  if (!e) return ['left'];
  const [a, b] = pipsOf(tile);
  const out: End[] = [];
  if (a === e[0] || b === e[0]) out.push('left');
  if (a === e[1] || b === e[1]) out.push('right');
  return out;
}

export interface Play { tile: Tile; end: End }

/** Every legal (tile, end) for this hand right now. On an empty line a tile has one entry (end 'left'). */
export function legalPlays(g: DominoesState, hand: Tile[]): Play[] {
  if (g.line.length === 0) {
    const ts = g.mustLead ? hand.filter((t) => t === g.mustLead) : hand;
    return ts.map((tile) => ({ tile, end: 'left' as End }));
  }
  const out: Play[] = [];
  const e = ends(g)!;
  for (const tile of hand) {
    for (const end of fitsOn(g, tile)) {
      // A tile that fits both ends identically (same end values) — list once.
      if (end === 'right' && e[0] === e[1] && out.some((p) => p.tile === tile)) continue;
      out.push({ tile, end });
    }
  }
  return out;
}
export const canPlay = (g: DominoesState, hand: Tile[]) => legalPlays(g, hand).length > 0;

function canPlayEnds(e: [number, number] | null, hand: Tile[]) {
  if (!e) return hand.length > 0;
  return hand.some((t) => { const [a, b] = pipsOf(t); return a === e[0] || b === e[0] || a === e[1] || b === e[1]; });
}

// ---------------------------------------------------------------------------
// Dealing
// ---------------------------------------------------------------------------

export interface Deal { state: DominoesState; hands: Tile[][]; boneyard: Tile[] }

/**
 * Deal a hand. `prev` carries the players, scores and next leader forward.
 * `players` (seated seats) and `target` are only read for a new game.
 */
export function deal(prev: DominoesState | null, rng: () => number, players: number[] = [0, 1, 2, 3], target = DEFAULT_TARGET): Deal {
  const ps = prev ? prev.players : players.slice().sort((a, b) => a - b);
  if (ps.length < 2 || ps.length > 4) throw new RuleError('Dominoes needs 2–4 players');
  const mode: DominoesState['mode'] = ps.length === 4 ? 'partners' : 'solo';
  const sides = prev ? prev.sides : mode === 'partners' ? [[0, 2], [1, 3]] : ps.map((s) => [s]);
  const set = shuffle(fullSet(), rng);
  const hands: Tile[][] = [[], [], [], []];
  ps.forEach((s, i) => { hands[s] = sortHand(set.slice(i * HAND_SIZE, (i + 1) * HAND_SIZE)); });
  const boneyard = set.slice(ps.length * HAND_SIZE);
  const counts = [0, 0, 0, 0];
  for (const s of ps) counts[s] = HAND_SIZE;

  let turn: number, mustLead: Tile | null = null;
  if (prev && prev.nextLeader !== null && ps.includes(prev.nextLeader)) {
    turn = prev.nextLeader;
  } else {
    // Highest double held sets; with no double anywhere, the heaviest tile.
    const held = ps.flatMap((s) => hands[s].map((t) => ({ s, t })));
    const best = held.slice().sort((x, y) =>
      (isDouble(y.t) ? 100 : 0) + valueOf(y.t) - ((isDouble(x.t) ? 100 : 0) + valueOf(x.t)) || Number(y.t[0]) - Number(x.t[0]))[0];
    turn = best.s; mustLead = best.t;
  }

  const state: DominoesState = {
    phase: 'playing', mode, players: ps, sides,
    target: prev ? prev.target : target,
    handNo: (prev?.handNo ?? 0) + 1,
    turn, line: [], first: 0, mustLead, counts, boneyard: boneyard.length,
    knocks: [[], [], [], []], last: null,
    scores: prev ? prev.scores.slice() : sides.map(() => 0),
    lastHand: null, nextLeader: null, winner: null,
  };
  return { state, hands, boneyard };
}

// ---------------------------------------------------------------------------
// Moves
// ---------------------------------------------------------------------------

export type DominoesMove = { type: 'play'; tile: string; end?: End } | { type: 'pass' } | { type: 'draw' };

/** Everyone's private tiles: hands[seat], plus the boneyard. */
export interface Hidden { hands: Tile[][]; boneyard: Tile[] }

function addKnock(knocks: number[][], seat: number, e: [number, number] | null): number[][] {
  if (!e) return knocks;
  const k = knocks.map((x) => x.slice());
  for (const v of e) if (!k[seat].includes(v)) k[seat].push(v);
  return k;
}

/** Score a finished hand (domino by `domino`, else a block). */
export function scoreHand(g: DominoesState, hands: Tile[][], domino: number | null): DominoesState {
  const pips = [0, 1, 2, 3].map((s) => (g.players.includes(s) ? handPips(hands[s] ?? []) : 0));
  let seat: number | null = domino;
  if (seat === null) {
    const low = Math.min(...g.players.map((s) => pips[s]));
    const lows = g.players.filter((s) => pips[s] === low);
    const lowSides = new Set(lows.map((s) => sideOf(g, s)));
    // Tied between sides: nobody scores. Same side: the first of them in turn order from the opener.
    seat = lowSides.size === 1 ? lows[0] : null;
  }
  const side = seat === null ? null : sideOf(g, seat);
  const raw = side === null ? 0 : g.players.filter((s) => sideOf(g, s) !== side).reduce((t, s) => t + pips[s], 0);
  const points = roundTo5(raw);
  const scores = g.scores.slice();
  if (side !== null) scores[side] += points;
  const winner = side !== null && scores[side] >= g.target ? side : null;
  // Next hand: the winner leads; after a tied block, whoever played the last tile.
  const lastPlay = g.line.length ? g.last && g.last.kind === 'play' ? g.last.seat : g.line[g.line.length - 1].seat : g.turn;
  return {
    ...g, scores,
    phase: winner === null ? 'hand_over' : 'game_over',
    winner,
    lastHand: { how: domino !== null ? 'domino' : 'block', seat, side, pips, raw, points, reveal: [0, 1, 2, 3].map((s) => (hands[s] ?? []).slice()) },
    nextLeader: seat ?? lastPlay,
  };
}

/** Nobody can play and nothing is left to draw. */
export function isBlocked(g: DominoesState, hands: Tile[][], boneyard: number): boolean {
  if (boneyard > 0 || g.line.length === 0) return false;
  const e = ends(g);
  return g.players.every((s) => !canPlayEnds(e, hands[s] ?? []));
}

/** After a play or a draw: end the hand on a block, else pass the turn on. */
function advance(g: DominoesState, hidden: Hidden, seat: number): DominoesState {
  if (isBlocked(g, hidden.hands, hidden.boneyard.length)) return scoreHand(g, hidden.hands, null);
  return { ...g, turn: nextPlayer(g, seat) };
}

export interface MoveOut { state: DominoesState; hands?: Record<number, Tile[]>; boneyard?: Tile[] }

export function applyMove(g: DominoesState, hidden: Hidden, seat: number, m: DominoesMove): MoveOut {
  if (g.phase !== 'playing') throw new RuleError('The hand is over');
  if (g.turn !== seat) throw new RuleError('Not your turn');
  const hand = hidden.hands[seat] ?? [];
  const type = (m as any)?.type;

  if (type === 'play') {
    const tile = parseTile((m as any).tile);
    if (!tile) throw new RuleError('Unknown tile');
    if (!hand.includes(tile)) throw new RuleError('You do not hold that tile');
    const [a, b] = pipsOf(tile);
    let line: Placed[], first = g.first, end: End;
    if (g.line.length === 0) {
      if (g.mustLead && tile !== g.mustLead) throw new RuleError(`The ${g.mustLead} must be set first`);
      line = [{ tile, l: a, r: b, seat }];
      first = 0;
      end = 'left';
    } else {
      const fits = fitsOn(g, tile);
      if (!fits.length) throw new RuleError('That tile does not match either end');
      const want = (m as any).end;
      if (want !== undefined && want !== 'left' && want !== 'right') throw new RuleError('End must be left or right');
      if (want && !fits.includes(want)) throw new RuleError(`That tile does not match the ${want} end`);
      const e = ends(g)!;
      if (!want && fits.length === 2 && e[0] !== e[1]) throw new RuleError('That tile fits both ends — pick one');
      end = want ?? fits[0];
      if (end === 'left') {
        const outer = a === e[0] ? b : a;
        line = [{ tile, l: outer, r: e[0], seat }, ...g.line];
        first = g.first + 1;
      } else {
        const outer = a === e[1] ? b : a;
        line = [...g.line, { tile, l: e[1], r: outer, seat }];
      }
    }
    const newHand = hand.filter((t) => t !== tile);
    const counts = g.counts.slice(); counts[seat] = newHand.length;
    let s: DominoesState = { ...g, line, first, mustLead: null, counts, last: { seat, kind: 'play', tile, end } };
    const hands = hidden.hands.slice(); hands[seat] = newHand;
    s = newHand.length === 0 ? scoreHand(s, hands, seat) : advance(s, { hands, boneyard: hidden.boneyard }, seat);
    return { state: s, hands: { [seat]: newHand } };
  }

  if (type === 'draw') {
    if (canPlay(g, hand)) throw new RuleError('You have a tile that plays');
    if (hidden.boneyard.length === 0) throw new RuleError('The boneyard is empty — pass');
    const yard = hidden.boneyard.slice();
    const newHand = hand.slice();
    let drew = 0;
    // Draw until something plays or the boneyard runs out.
    while (yard.length && !canPlay(g, newHand)) { newHand.push(yard.shift()!); drew++; }
    const playable = canPlay(g, newHand);
    const counts = g.counts.slice(); counts[seat] = newHand.length;
    const sorted = sortHand(newHand);
    let s: DominoesState = { ...g, counts, boneyard: yard.length, last: { seat, kind: 'draw', drew, passed: !playable } };
    if (!playable) {
      // Boneyard gone and still nothing: an automatic pass.
      s = { ...s, knocks: addKnock(g.knocks, seat, ends(g)) };
      const hands = hidden.hands.slice(); hands[seat] = sorted;
      s = advance(s, { hands, boneyard: yard }, seat);
    }
    return { state: s, hands: { [seat]: sorted }, boneyard: yard };
  }

  if (type === 'pass') {
    if (canPlay(g, hand)) throw new RuleError('You have a tile that plays');
    if (hidden.boneyard.length > 0) throw new RuleError('Draw from the boneyard first');
    const s: DominoesState = { ...g, knocks: addKnock(g.knocks, seat, ends(g)), last: { seat, kind: 'pass' } };
    return { state: advance(s, hidden, seat) };
  }

  throw new RuleError('Unknown move');
}

// ---------------------------------------------------------------------------
// Computer players
// ---------------------------------------------------------------------------

/**
 * A sensible bot: set heavy tiles (and doubles, which are hard to place later)
 * early, keep ends it can match, avoid ends its partner has knocked on, and
 * prefer ends the next opponent has knocked on.
 */
export function botMove(g: DominoesState, hand: Tile[], seat: number): DominoesMove {
  const legal = legalPlays(g, hand);
  if (!legal.length) return g.boneyard > 0 ? { type: 'draw' } : { type: 'pass' };
  if (legal.length === 1) return { type: 'play', tile: legal[0].tile, end: legal[0].end };
  const partner = partnerOf(g, seat);
  const nextOpp = (() => {
    let s = nextPlayer(g, seat);
    while (s !== seat && sideOf(g, s) === sideOf(g, seat)) s = nextPlayer(g, s);
    return s === seat ? -1 : s;
  })();
  const e = ends(g);
  let best = legal[0], bestScore = -Infinity;
  for (const p of legal) {
    const [a, b] = pipsOf(p.tile);
    let newEnds: [number, number];
    if (!e) newEnds = [a, b];
    else if (p.end === 'left') newEnds = [a === e[0] ? b : a, e[1]];
    else newEnds = [e[0], a === e[1] ? b : a];
    const rest = hand.filter((t) => t !== p.tile);
    let score = valueOf(p.tile) + (isDouble(p.tile) ? 4 : 0);
    // Keep ends I can follow: tiles left that match the new ends.
    const follow = rest.filter((t) => { const [x, y] = pipsOf(t); return newEnds.includes(x) || newEnds.includes(y); }).length;
    score += 2.5 * follow;
    // Hold a "suit" I'm long in: count tiles of each new end value.
    for (const v of new Set(newEnds)) {
      const mine = rest.filter((t) => pipsOf(t).includes(v)).length;
      score += mine >= 3 ? 1.5 : 0;
      if (nextOpp >= 0 && g.knocks[nextOpp]?.includes(v)) score += 3;   // they can't play on it
      if (partner >= 0 && g.knocks[partner]?.includes(v)) score -= 3;   // nor can my partner
    }
    // Don't shut my partner out: leave the end my partner just set if I can.
    if (partner >= 0 && g.last?.kind === 'play' && g.last.seat === partner && e) {
      const partnerEnd = g.last.end === 'left' ? e[0] : e[1];
      if (p.end === g.last.end && !newEnds.includes(partnerEnd)) score -= 1.5;
    }
    if (score > bestScore) { bestScore = score; best = p; }
  }
  return { type: 'play', tile: best.tile, end: best.end };
}

// ---------------------------------------------------------------------------
// Table framework hookup (registered in registry.ts)
// ---------------------------------------------------------------------------

const handsOf = (privs: Record<number, any>): Hidden => ({
  hands: [0, 1, 2, 3].map((s) => (Array.isArray(privs[s]) ? (privs[s] as Tile[]) : [])),
  boneyard: Array.isArray(privs[BONEYARD_SEAT]) ? (privs[BONEYARD_SEAT] as Tile[]) : [],
});

export const dominoesGame: GameDef<DominoesState, DominoesMove> = {
  kind: 'dominoes',
  label: 'Dominoes',
  minPlayers: 2,
  maxPlayers: 4,
  seatNames: SEAT_NAMES,
  configure: (cur, patch) => {
    const t = Number((patch as any)?.target);
    if (!TARGETS.includes(t as any)) throw new RuleError('Play to 100, 150 or 200');
    return { ...(cur ?? {}), target: t };
  },
  start: (prev, ctx) => {
    const players = ctx.seats.map((s, i) => (s ? i : -1)).filter((i) => i >= 0);
    const target = TARGETS.includes(Number(ctx.options?.target) as any) ? Number(ctx.options.target) : DEFAULT_TARGET;
    const { state, hands, boneyard } = deal(prev, ctx.rng, players, target);
    const privs: Record<number, any> = {};
    for (const s of state.players) privs[s] = hands[s];
    privs[BONEYARD_SEAT] = boneyard;
    return { g: state, privs };
  },
  turnSeat: (g) => (g.phase === 'playing' ? g.turn : null),
  move: (g, privs, seat, m) => {
    const r = applyMove(g, handsOf(privs), seat, m);
    const out: Record<number, any> = { ...(r.hands ?? {}) };
    if (r.boneyard) out[BONEYARD_SEAT] = r.boneyard;
    return { g: r.state, privs: Object.keys(out).length ? out : undefined };
  },
  bot: {
    move: (g, hand: Tile[] | null, seat) => botMove(g, Array.isArray(hand) ? hand : [], seat),
    names: ['Bones Bot', 'Domino Bot', 'Spinner Bot', 'Rattle Bot'],
  },
  betweenHands: (g) => g.phase === 'hand_over',
  betweenHandsMs: 8_000,
  isOver: (g) => g.phase === 'game_over',
  results: (g, ctx) => g.players.filter((s) => ctx.seats[s]).map((seat): SeatResult => {
    const side = sideOf(g, seat);
    return {
      seat,
      outcome: g.winner === null ? 'draw' : side === g.winner ? 'win' : 'loss',
      points: g.scores[side] ?? 0,
    };
  }),
  // Let the last tile of a hand land before the scores take over.
  pauseAfter: (_prev, g) => (g.phase !== 'playing' ? 600 : 0),
  describe: (m, who) => m.type === 'play' ? `${who} played ${parseTile(m.tile) ?? ''}`.trim()
    : m.type === 'draw' ? `${who} drew from the boneyard` : `${who} passed`,
};
